import axios from 'axios';

// API base URL - uses relative path in production (served through Nginx proxy)
// In development, set REACT_APP_API_URL=http://localhost:8000/api
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      // Don't redirect on public routes
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============ Public API ============

export const portfolioApi = {
  // Get all portfolio data in one call
  getAll: async () => {
    const response = await api.get('/portfolio');
    return response.data;
  },

  // Get individual sections
  getPersonalInfo: async () => {
    const response = await api.get('/personal-info');
    return response.data;
  },

  getSkills: async () => {
    const response = await api.get('/skills');
    return response.data;
  },

  getExperience: async () => {
    const response = await api.get('/experience');
    return response.data;
  },

  getProjects: async () => {
    const response = await api.get('/projects');
    return response.data;
  },

  getEducation: async () => {
    const response = await api.get('/education');
    return response.data;
  },

  getCertifications: async () => {
    const response = await api.get('/certifications');
    return response.data;
  },

  getSettings: async () => {
    const response = await api.get('/settings');
    return response.data;
  },

  // Contact form
  submitContact: async (data) => {
    const response = await api.post('/contact', data);
    return response.data;
  },
};

// ============ Auth API ============

export const authApi = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },

  verify: async () => {
    const response = await api.get('/auth/verify');
    return response.data;
  },
};

// ============ Admin API ============

export const adminApi = {
  // Dashboard stats
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  // Personal Info
  updatePersonalInfo: async (data) => {
    const response = await api.put('/admin/personal-info', data);
    return response.data;
  },

  // Skills
  updateSkills: async (data) => {
    const response = await api.put('/admin/skills', data);
    return response.data;
  },

  // Experience
  getExperience: async () => {
    const response = await api.get('/admin/experience');
    return response.data;
  },

  createExperience: async (data) => {
    const response = await api.post('/admin/experience', data);
    return response.data;
  },

  updateExperience: async (id, data) => {
    const response = await api.put(`/admin/experience/${id}`, data);
    return response.data;
  },

  deleteExperience: async (id) => {
    const response = await api.delete(`/admin/experience/${id}`);
    return response.data;
  },

  // Projects
  getProjects: async () => {
    const response = await api.get('/admin/projects');
    return response.data;
  },

  createProject: async (data) => {
    const response = await api.post('/admin/projects', data);
    return response.data;
  },

  updateProject: async (id, data) => {
    const response = await api.put(`/admin/projects/${id}`, data);
    return response.data;
  },

  deleteProject: async (id) => {
    const response = await api.delete(`/admin/projects/${id}`);
    return response.data;
  },

  // Education
  getEducation: async () => {
    const response = await api.get('/admin/education');
    return response.data;
  },

  createEducation: async (data) => {
    const response = await api.post('/admin/education', data);
    return response.data;
  },

  updateEducation: async (id, data) => {
    const response = await api.put(`/admin/education/${id}`, data);
    return response.data;
  },

  deleteEducation: async (id) => {
    const response = await api.delete(`/admin/education/${id}`);
    return response.data;
  },

  // Certifications
  getCertifications: async () => {
    const response = await api.get('/admin/certifications');
    return response.data;
  },

  createCertification: async (data) => {
    const response = await api.post('/admin/certifications', data);
    return response.data;
  },

  updateCertification: async (id, data) => {
    const response = await api.put(`/admin/certifications/${id}`, data);
    return response.data;
  },

  deleteCertification: async (id) => {
    const response = await api.delete(`/admin/certifications/${id}`);
    return response.data;
  },

  // Messages
  getMessages: async () => {
    const response = await api.get('/admin/messages');
    return response.data;
  },

  markMessageRead: async (id) => {
    const response = await api.put(`/admin/messages/${id}/read`);
    return response.data;
  },

  deleteMessage: async (id) => {
    const response = await api.delete(`/admin/messages/${id}`);
    return response.data;
  },

  // Settings
  updateSettings: async (data) => {
    const response = await api.put('/admin/settings', data);
    return response.data;
  },

  // File upload
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/admin/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export default api;
